import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect, useCallback } from 'react';

import {
  Box,
  Tab,
  Tabs,
  List,
  Dialog,
  Avatar,
  Button,
  Divider,
  Checkbox,
  ListItem,
  TextField,
  Typography,
  DialogTitle,
  ListItemText,
  DialogContent,
  DialogActions,
  ListItemAvatar,
  InputAdornment,
  CircularProgress,
} from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useToast } from 'src/hooks/useToast';

import { getUserAvatarUrl } from 'src/services/user.service';
import {
  bulkAssignMembers,
  fetchAvailableGroups,
  fetchAvailableMembers,
} from 'src/services/department.service';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';

AddMembersModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  department: PropTypes.object,
  availableUsers: PropTypes.array,
  availableGroups: PropTypes.array,
  onSubmit: PropTypes.func,
};

export default function AddMembersModal({
  open,
  onClose,
  department,
  availableUsers: propsUsers,
  availableGroups: propsGroups,
  onSubmit,
}) {
  const { t } = useTranslation();
  const { showToast, hideToast } = useToast();
  const { id: currentUserId } = useAuth();

  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  const [internalUsers, setInternalUsers] = useState([]);
  const [internalGroups, setInternalGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAvailableData = useCallback(async () => {
    if (!department?.id || !open) return;

    setLoading(true);
    try {
      const [users, groups] = await Promise.all([
        fetchAvailableMembers(department.id),
        fetchAvailableGroups(),
      ]);
      setInternalUsers(users);
      setInternalGroups(groups);
    } catch (error) {
      console.error('Failed to load candidates', error);
    } finally {
      setLoading(false);
    }
  }, [department?.id, open]);

  useEffect(() => {
    if (!propsUsers || !propsGroups) {
      loadAvailableData();
    }
  }, [propsUsers, propsGroups, loadAvailableData]);

  const users = propsUsers || internalUsers;
  const groups = propsGroups || internalGroups;

  const filteredUsers = useMemo(
    () =>
      users.filter((user) => {
        const name = `${user.givenName || ''} ${user.familyName || ''}`.toLowerCase();
        const username = (user.username || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const query = searchQuery.toLowerCase();

        return name.includes(query) || username.includes(query) || email.includes(query);
      }),
    [users, searchQuery]
  );

  const filteredGroups = useMemo(
    () =>
      groups.filter((group) => {
        const matchesQuery = (group.name || '')
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
        const isCreatedBySelf = String(group.createdBy) === String(currentUserId);
        return matchesQuery && isCreatedBySelf;
      }),
    [groups, searchQuery, currentUserId]
  );

  const toggleUser = (id) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleGroup = (id) => {
    setSelectedGroupIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleConfirm = async () => {
    if (onSubmit) {
      onSubmit(selectedUserIds, selectedGroupIds);
    } else {
      setLoading(true);
      try {
        await bulkAssignMembers(department.id, {
          userIds: selectedUserIds,
          groupIds: selectedGroupIds,
        });
        showToast({
          message: t('successMessages.membersAddedSuccessfully'),
          severity: 'success',
          props: { hideToast },
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }

    setSearchQuery('');
    setSelectedUserIds([]);
    setSelectedGroupIds([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '24px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(22, 27, 34, 0.9)'
              : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }} component="div">
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {t('ticket.addMembersTo')} {department?.name}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ fontWeight: 600 }}
        >
          {t('ticket.batchAssignmentInstruction')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: 3, py: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('filters.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label={`${t('nav.users')} (${filteredUsers.length})`}
            sx={{ fontWeight: 700 }}
          />
          <Tab
            label={`${t('ticket.groups')} (${filteredGroups.length})`}
            sx={{ fontWeight: 700 }}
          />
        </Tabs>

        <Scrollbar sx={{ maxHeight: 400, px: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress color="success" />
            </Box>
          ) : (
            <>
              {activeTab === 0 ? (
                <List>
                  {filteredUsers.map((user) => (
                    <ListItem
                      key={user.id}
                      button
                      onClick={() => toggleUser(user.id)}
                      secondaryAction={
                        <Checkbox
                          edge="end"
                          checked={selectedUserIds.includes(user.id)}
                          disableRipple
                          sx={{ color: '#00A76F', '&.Mui-checked': { color: '#00A76F' } }}
                        />
                      }
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={
                            user.avatarVersion
                              ? getUserAvatarUrl(user.id, user.avatarVersion)
                              : null
                          }
                          sx={{ border: '1px solid', borderColor: 'divider' }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${user.givenName || ''} ${user.familyName || ''}`}
                        secondary={user.email}
                        primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 800 }}
                        secondaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <List>
                  {filteredGroups.map((group) => (
                    <ListItem
                      key={group.id}
                      button
                      onClick={() => toggleGroup(group.id)}
                      secondaryAction={
                        <Checkbox
                          edge="end"
                          checked={selectedGroupIds.includes(group.id)}
                          disableRipple
                          sx={{ color: '#00A76F', '&.Mui-checked': { color: '#00A76F' } }}
                        />
                      }
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{ bgcolor: 'rgba(0, 167, 111, 0.1)', color: '#00A76F' }}
                        >
                          <Iconify icon="solar:users-group-two-rounded-bold-duotone" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={group.name}
                        secondary={`${group.users?.length || 0} ${t('nav.users')}`}
                        primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 800 }}
                        secondaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </Scrollbar>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2.5 }}>
        <Button color="inherit" onClick={onClose} sx={{ fontWeight: 700 }}>
          {t('buttons.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={
            loading || (selectedUserIds.length === 0 && selectedGroupIds.length === 0)
          }
          onClick={handleConfirm}
          sx={{
            bgcolor: '#00A76F',
            fontWeight: 800,
            borderRadius: '12px',
            '&:hover': { bgcolor: '#008F5D' },
          }}
        >
          {t('buttons.confirm')} ({selectedUserIds.length + selectedGroupIds.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
